import React, { useEffect, useRef } from 'react';
import MessageBubble from './MessageBubble';

/**
 * MessageList — the scrollable thread in the conversation rail (Epic 7 · S7.2,
 * Part A.5).
 *
 * Presentational. The owning ConversationPanel owns the messages array and the
 * olderMessages / markRead data flow; MessageList just renders.
 *
 * Behaviour:
 *   - Messages oldest-first (the parent keeps them in that order).
 *   - Opens scrolled to the newest (bottom) — a ref + layout effect on
 *     messages.length so new messages / older prepends both land right.
 *   - “Load older” button at the top → calls `onLoadOlder`; hidden when
 *     `!hasMore`. Shows “Loading…” while `loadingOlder`.
 *   - Empty list → “No messages yet.” sentence.
 *
 * `onFetchUrl` is passed through to MessageBubble/FileChip for testability.
 */
const MessageList = ({ messages, hasMore, loadingOlder, onLoadOlder, onOpenItem, onFetchUrl }) => {
  const list = Array.isArray(messages) ? messages : [];
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);

  // Scroll to the newest message whenever the count changes (new send OR older
  // prepend). requestAnimationFrame avoids the layout pass reading a stale height.
  useEffect(() => {
    if (list.length === 0) return;
    const node = bottomRef.current;
    if (node && typeof node.scrollIntoView === 'function') {
      requestAnimationFrame(() => node.scrollIntoView({ block: 'end' }));
    }
  }, [list.length]);

  if (list.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-4" data-testid="message-list-empty">
        No messages yet.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="message-list" ref={scrollRef}>
      {hasMore && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onLoadOlder}
            disabled={loadingOlder}
            className="text-xs text-[#32B1FF] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="load-older"
          >
            {loadingOlder ? 'Loading…' : 'Load older'}
          </button>
        </div>
      )}
      {list.map((m, i) => (
        <MessageBubble key={m?.id ?? i} msg={m} onOpenItem={onOpenItem} onFetchUrl={onFetchUrl} />
      ))}
      <div ref={bottomRef} />
    </div>
  );
};

export default MessageList;
